import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Mail,
  TrendingUp,
  TrendingDown,
  MousePointer,
  Eye,
  Send,
  AlertCircle,
  Calendar,
  Smartphone,
  Monitor,
  Tablet,
  Users,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const EmailAnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [interval, setInterval] = useState('day');

  // Estados para dados
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [byType, setByType] = useState([]);
  const [byClient, setByClient] = useState([]);
  const [devices, setDevices] = useState([]);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, interval]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };

      // Carregar todas as métricas em paralelo
      const [statsRes, timelineRes, byTypeRes, byClientRes, devicesRes] = await Promise.all([
        axios.get('/api/email-tracking/stats', { params }),
        axios.get('/api/email-tracking/metrics/timeline', { params: { ...params, interval } }),
        axios.get('/api/email-tracking/metrics/by-type', { params }),
        axios.get('/api/email-tracking/metrics/by-client', { params, limit: 10 }),
        axios.get('/api/email-tracking/metrics/devices', { params })
      ]);

      setStats(statsRes.data.data);
      setTimeline(timelineRes.data.data);
      setByType(byTypeRes.data.data);
      setByClient(byClientRes.data.data);
      setDevices(devicesRes.data.data);

    } catch (error) {
      console.error('Erro ao carregar analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Preparar dados para o gráfico de timeline
  const timelineChartData = {
    labels: timeline.map(t => t.period),
    datasets: [
      {
        label: 'Enviados',
        data: timeline.map(t => t.sent),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Abertos',
        data: timeline.map(t => t.opened),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Clicados',
        data: timeline.map(t => t.clicked),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  // Dados para gráfico de tipos de email
  const emailTypeChartData = {
    labels: byType.map(t => t.email_type || 'Outros'),
    datasets: [{
      label: 'Total Enviado',
      data: byType.map(t => t.total),
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(168, 85, 247, 0.8)',
        'rgba(251, 146, 60, 0.8)',
        'rgba(236, 72, 153, 0.8)',
      ]
    }]
  };

  // Dados para gráfico de dispositivos
  const deviceChartData = {
    labels: devices.map(d => {
      if (d.device_type === 'mobile') return 'Mobile';
      if (d.device_type === 'desktop') return 'Desktop';
      if (d.device_type === 'tablet') return 'Tablet';
      return 'Outros';
    }),
    datasets: [{
      data: devices.map(d => d.total),
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(251, 146, 60, 0.8)',
        'rgba(168, 85, 247, 0.8)',
      ]
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      }
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Activity className="w-12 h-12 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600 dark:text-gray-400">Carregando analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          📊 Email Analytics
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Análise de performance de emails enviados
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Data Inicial
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Data Final
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Intervalo
            </label>
            <select
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="hour">Por Hora</option>
              <option value="day">Por Dia</option>
              <option value="week">Por Semana</option>
              <option value="month">Por Mês</option>
            </select>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Enviado */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <Send className="w-8 h-8 opacity-80" />
              <span className="text-2xl font-bold">{stats.total}</span>
            </div>
            <div className="text-sm opacity-90">Total Enviado</div>
            <div className="text-xs opacity-75 mt-1">
              Sucesso: {stats.sent} | Falha: {stats.failed}
            </div>
          </div>

          {/* Taxa de Abertura */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <Eye className="w-8 h-8 opacity-80" />
              <span className="text-2xl font-bold">{stats.open_rate}%</span>
            </div>
            <div className="text-sm opacity-90">Taxa de Abertura</div>
            <div className="text-xs opacity-75 mt-1">
              {stats.opened} de {stats.sent} emails
            </div>
          </div>

          {/* Taxa de Cliques */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <MousePointer className="w-8 h-8 opacity-80" />
              <span className="text-2xl font-bold">{stats.click_rate}%</span>
            </div>
            <div className="text-sm opacity-90">Taxa de Cliques</div>
            <div className="text-xs opacity-75 mt-1">
              {stats.clicked} de {stats.sent} emails
            </div>
          </div>

          {/* Total de Aberturas */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <span className="text-2xl font-bold">{stats.total_opens}</span>
            </div>
            <div className="text-sm opacity-90">Total de Aberturas</div>
            <div className="text-xs opacity-75 mt-1">
              Média: {(stats.total_opens / (stats.opened || 1)).toFixed(1)} por email
            </div>
          </div>
        </div>
      )}

      {/* Gráfico de Timeline */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
          <Activity className="w-5 h-5 mr-2" />
          Tendência ao Longo do Tempo
        </h2>
        <div className="h-80">
          <Line data={timelineChartData} options={chartOptions} />
        </div>
      </div>

      {/* Grid de 2 colunas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Emails por Tipo */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
            <Mail className="w-5 h-5 mr-2" />
            Performance por Tipo de Email
          </h2>
          <div className="h-80">
            <Bar data={emailTypeChartData} options={chartOptions} />
          </div>

          {/* Tabela de detalhes */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b dark:border-gray-700">
                  <th className="text-left py-2 text-gray-700 dark:text-gray-300">Tipo</th>
                  <th className="text-right py-2 text-gray-700 dark:text-gray-300">Enviados</th>
                  <th className="text-right py-2 text-gray-700 dark:text-gray-300">Abertos</th>
                  <th className="text-right py-2 text-gray-700 dark:text-gray-300">Taxa</th>
                </tr>
              </thead>
              <tbody>
                {byType.map((type, idx) => (
                  <tr key={idx} className="border-b dark:border-gray-700">
                    <td className="py-2 text-gray-900 dark:text-white font-medium">
                      {type.email_type || 'Outros'}
                    </td>
                    <td className="text-right text-gray-600 dark:text-gray-400">{type.sent}</td>
                    <td className="text-right text-gray-600 dark:text-gray-400">{type.opened}</td>
                    <td className="text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        type.open_rate >= 30 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        type.open_rate >= 15 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {type.open_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Dispositivos */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
            <Smartphone className="w-5 h-5 mr-2" />
            Distribuição por Dispositivo
          </h2>
          <div className="h-80 flex items-center justify-center">
            <Doughnut data={deviceChartData} options={chartOptions} />
          </div>

          {/* Lista de dispositivos */}
          <div className="mt-6 space-y-3">
            {devices.map((device, idx) => {
              const icon = device.device_type === 'mobile' ? Smartphone :
                          device.device_type === 'desktop' ? Monitor :
                          device.device_type === 'tablet' ? Tablet : Smartphone;
              const Icon = icon;

              return (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center">
                    <Icon className="w-5 h-5 mr-3 text-gray-600 dark:text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {device.device_type === 'mobile' ? 'Mobile' :
                         device.device_type === 'desktop' ? 'Desktop' :
                         device.device_type === 'tablet' ? 'Tablet' : 'Outros'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {device.opened} aberturas
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {device.total}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {device.open_rate}% abertos
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top Clientes */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2" />
          Top 10 Clientes por Engajamento
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b dark:border-gray-700">
                <th className="text-left py-3 px-4 text-gray-700 dark:text-gray-300">Cliente</th>
                <th className="text-left py-3 px-4 text-gray-700 dark:text-gray-300">Email</th>
                <th className="text-right py-3 px-4 text-gray-700 dark:text-gray-300">Total Emails</th>
                <th className="text-right py-3 px-4 text-gray-700 dark:text-gray-300">Abertos</th>
                <th className="text-right py-3 px-4 text-gray-700 dark:text-gray-300">Clicados</th>
                <th className="text-right py-3 px-4 text-gray-700 dark:text-gray-300">Engajamento</th>
                <th className="text-left py-3 px-4 text-gray-700 dark:text-gray-300">Último Email</th>
              </tr>
            </thead>
            <tbody>
              {byClient.map((client, idx) => (
                <tr key={idx} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="py-3 px-4">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {client.recipient_name || 'N/A'}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-600 dark:text-gray-400 text-sm">
                    {client.recipient_email}
                  </td>
                  <td className="text-right py-3 px-4 text-gray-900 dark:text-white font-medium">
                    {client.total_emails}
                  </td>
                  <td className="text-right py-3 px-4 text-gray-600 dark:text-gray-400">
                    {client.opened_emails}
                  </td>
                  <td className="text-right py-3 px-4 text-gray-600 dark:text-gray-400">
                    {client.clicked_emails}
                  </td>
                  <td className="text-right py-3 px-4">
                    <div className="flex items-center justify-end">
                      <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                        <div
                          className={`h-2 rounded-full ${
                            client.engagement_score >= 50 ? 'bg-green-500' :
                            client.engagement_score >= 25 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${client.engagement_score}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {client.engagement_score}%
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {new Date(client.last_email_sent).toLocaleDateString('pt-BR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer com legenda */}
      <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-2">Sobre as Métricas</p>
            <ul className="space-y-1 text-xs">
              <li><strong>Taxa de Abertura:</strong> Porcentagem de emails abertos em relação aos enviados</li>
              <li><strong>Taxa de Cliques:</strong> Porcentagem de emails com cliques em relação aos enviados</li>
              <li><strong>Engajamento:</strong> Score baseado na taxa de abertura do cliente (0-100%)</li>
              <li><strong>Total de Aberturas:</strong> Número total de vezes que emails foram abertos (pode ser &gt; que emails enviados)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailAnalyticsPage;
